/**
 * Share links — the atomic unit of growth.
 *
 * A shared card must open INSIDE the feed, positioned at that card, already playing. Not the
 * bare page: a stranger who lands on /cards/<slug> sees one page with no way onward, and the
 * whole mechanism (bait with one card, hook with the next press of BLITZ) never happens.
 *
 * So a share is  https://blitzfeed.app/c/<slug>?r=<referrer>
 *   /c/<slug>   loads the app and starts at that card
 *   ?r=         who shared it — recorded on every telemetry row for the rest of the session,
 *               so the edge exists from the first share and sharers can be credited later.
 *   ?via=       which door: 'pilot', 'landing', a community's name — recorded beside ?r= as
 *               entry_source on every row, the only attribution a cohort has. A short slug,
 *               captured once per session, dropped from the address bar like ?r=.
 *
 * The address bar is kept in sync with the current card (without ?r), so copying it is also a
 * valid, if uncredited, share.
 */

export const CARD_PATH_PREFIX = '/c/';
const REFERRER_KEY = 'blitz.referrer';
const ENTRY_KEY = 'blitz.entry';
const VIA_KEY = 'blitz.via';

export type EntrySurface = 'direct' | 'share' | 'landing';

const SLUG = /^[a-z0-9][a-z0-9-]{0,80}$/i;
/**
 * Referrer ids are user ids, which are minted as `u_<random>` — the underscore matters. A
 * validator that only admits slug characters silently drops every real referrer, which the
 * unit test caught before it could drop every real share.
 */
const USER_ID = /^[a-z0-9][a-z0-9_-]{0,80}$/i;
/** The server keeps an entry_source only in this shape (ingest_batch); anything else is dropped here too. */
const VIA = /^[a-z0-9_-]{1,32}$/;

/** The card a URL asks for: /c/<slug>, or the older ?game=<id>. Null when neither. */
export function cardFromLocation(loc: { pathname: string; search: string } = location): string | null {
  const path = loc.pathname;
  if (path.startsWith(CARD_PATH_PREFIX)) {
    const slug = decodeURIComponent(path.slice(CARD_PATH_PREFIX.length)).replace(/\/+$/, '');
    if (SLUG.test(slug)) return slug;
  }
  const q = new URLSearchParams(loc.search).get('game');
  return q && SLUG.test(q) ? q : null;
}

/**
 * Capture attribution from the URL once, on first load, and keep it for the session.
 * Returns what was recorded. Safe to call repeatedly; only the first call in a session writes.
 */
export function captureEntry(loc: { pathname: string; search: string } = location): {
  referrer: string | null;
  entry: EntrySurface;
  via: string | null;
} {
  let referrer: string | null = null;
  let entry: EntrySurface = 'direct';
  let via: string | null = null;
  try {
    const existing = sessionStorage.getItem(ENTRY_KEY);
    if (existing) {
      return { referrer: sessionStorage.getItem(REFERRER_KEY), entry: existing as EntrySurface, via: sessionStorage.getItem(VIA_KEY) };
    }
    const params = new URLSearchParams(loc.search);
    const v = params.get('via');
    if (v && VIA.test(v)) via = v;
    const r = params.get('r');
    if (r && USER_ID.test(r)) {
      referrer = r;
      entry = 'share';
    } else if (loc.pathname.startsWith(CARD_PATH_PREFIX)) {
      entry = 'share'; // a card link with no referrer is still a share, just uncredited
    } else if (fromLanding(document.referrer)) {
      entry = 'landing';
    }
    sessionStorage.setItem(ENTRY_KEY, entry);
    if (referrer) sessionStorage.setItem(REFERRER_KEY, referrer);
    if (via) sessionStorage.setItem(VIA_KEY, via);
  } catch {
    /* storage unavailable: attribution is best-effort */
  }
  return { referrer, entry, via };
}

/** The landing page is the site root (and still answers at /landing); only our own root counts. */
function fromLanding(referrer: string): boolean {
  if (!referrer) return false;
  try {
    const u = new URL(referrer);
    if (typeof location !== 'undefined' && u.origin !== location.origin) return false;
    return u.pathname === '/' || u.pathname.startsWith('/landing');
  } catch {
    return referrer.includes('/landing');
  }
}

/** The recorded attribution for this session, for stamping onto telemetry rows. */
export function entryContext(): { referrer_user_id: string | null; entry_surface: EntrySurface; entry_source: string | null } {
  try {
    return {
      referrer_user_id: sessionStorage.getItem(REFERRER_KEY),
      entry_surface: (sessionStorage.getItem(ENTRY_KEY) as EntrySurface | null) ?? 'direct',
      entry_source: sessionStorage.getItem(VIA_KEY),
    };
  } catch {
    return { referrer_user_id: null, entry_surface: 'direct', entry_source: null };
  }
}

/** The link to share for a card. */
export function shareUrl(slug: string, referrerId: string, origin: string = location.origin): string {
  const u = new URL(CARD_PATH_PREFIX + encodeURIComponent(slug), origin);
  u.searchParams.set('r', referrerId);
  return u.toString();
}

/**
 * Keep the address bar on the current card. replaceState, not pushState: pressing BLITZ ten
 * times must not make the browser back button take ten presses to leave.
 */
export function syncUrlToCard(slug: string): void {
  try {
    const target = CARD_PATH_PREFIX + encodeURIComponent(slug);
    // Preserve ?dev=1 and similar; drop the referrer and the via so the bar is a clean
    // uncredited link (a copied bar link or a reload must not re-tag the next session), and
    // the one-shot flags (?signup, ?signin, ?make) so a reload does not reopen the sheet.
    const params = new URLSearchParams(location.search);
    for (const k of ['r', 'via', 'game', 'signup', 'signin', 'account', 'link', 'make']) params.delete(k);
    const qs = params.toString();
    // Never carry an auth fragment (confirmation tokens, auth errors) onto a card link.
    const hash = /(^|[#&])(access_token|refresh_token|error|error_description|type)=/.test(location.hash) ? '' : location.hash;
    const next = target + (qs ? `?${qs}` : '') + hash;
    if (location.pathname + location.search + location.hash !== next) {
      history.replaceState(history.state, '', next);
    }
  } catch {
    /* history unavailable (sandboxed) — the feed still works, the bar just does not follow */
  }
}

/**
 * Share via the OS sheet where it exists, otherwise copy the link. Returns how it was shared,
 * or 'cancelled' when the user dismissed the sheet, so the caller can decide what to say.
 */
export async function shareCard(opts: {
  slug: string;
  title: string;
  referrerId: string;
}): Promise<'shared' | 'copied' | 'cancelled' | 'unavailable'> {
  const url = shareUrl(opts.slug, opts.referrerId);
  const nav = navigator as Navigator & { share?: (d: ShareData) => Promise<void>; canShare?: (d: ShareData) => boolean };
  // One line for every kind of card — a picture, a clip, a page, a game: nothing is promised
  // that the card may not be.
  const data: ShareData = { title: `${opts.title} — Blitz`, text: 'Found this on Blitz.', url };
  if (typeof nav.share === 'function' && (!nav.canShare || nav.canShare(data))) {
    try {
      await nav.share(data);
      return 'shared';
    } catch (err) {
      if ((err as Error).name === 'AbortError') return 'cancelled';
      /* fall through to clipboard */
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return 'copied';
  } catch {
    return 'unavailable';
  }
}
